import React, { useState, useMemo } from 'react';
import { useProject } from '@/contexts/ProjectContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  MessageCircle, 
  Activity, 
  Plus, 
  Edit, 
  Trash2, 
  CheckCircle,
  Clock,
  User,
  FileText,
  AlertCircle,
  GitCommit,
  Calendar,
  Send,
  Filter
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ActivityItem {
  id: string;
  type: 'task_created' | 'task_updated' | 'task_completed' | 'project_created' | 'project_updated' | 'comment_added' | 'user_assigned' | 'deadline_changed';
  userId: string;
  targetId: string;
  targetType: 'task' | 'project';
  title: string;
  description: string;
  timestamp: Date;
  metadata?: Record<string, any>;
}

interface Comment {
  id: string;
  taskId: string;
  userId: string;
  content: string;
  timestamp: Date;
  edited?: boolean;
}

export const ActivityFeed: React.FC = () => {
  const { projects, tasks } = useProject();
  const { user } = useAuth();
  const [filter, setFilter] = useState<'all' | 'tasks' | 'projects' | 'comments'>('all');
  const [newComment, setNewComment] = useState('');
  const [selectedTask, setSelectedTask] = useState<string>('');

  // Mock users
  const mockUsers = [
    { id: '1', name: 'Admin User', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face' },
    { id: '2', name: 'Manager User', avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b865?w=32&h=32&fit=crop&crop=face' },
    { id: '3', name: 'Collaborator User', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face' }
  ];

  const getUserById = (userId: string) => mockUsers.find(u => u.id === userId);

  // Mock activities
  const [activities] = useState<ActivityItem[]>([
    {
      id: '1',
      type: 'task_completed',
      userId: '2',
      targetId: '1',
      targetType: 'task',
      title: 'Tarefa concluída',
      description: 'Manager User concluiu a tarefa "Análise de Requisitos"',
      timestamp: new Date(Date.now() - 1000 * 60 * 30), // 30 minutes ago
    },
    {
      id: '2',
      type: 'task_created',
      userId: '1',
      targetId: '3',
      targetType: 'task',
      title: 'Nova tarefa criada',
      description: 'Admin User criou a tarefa "Implementação Frontend"',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
    },
    {
      id: '3',
      type: 'user_assigned',
      userId: '1',
      targetId: '2',
      targetType: 'task',
      title: 'Usuário atribuído',
      description: 'Admin User atribuiu Collaborator User à tarefa "Criação de Wireframes"',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 4), // 4 hours ago
    },
    {
      id: '4',
      type: 'project_created',
      userId: '2',
      targetId: '2',
      targetType: 'project',
      title: 'Projeto criado',
      description: 'Manager User criou o projeto "Mobile App Development"',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 6), // 6 hours ago
    },
    {
      id: '5',
      type: 'deadline_changed',
      userId: '1',
      targetId: '4',
      targetType: 'task',
      title: 'Prazo alterado',
      description: 'Admin User alterou o prazo da tarefa "Testes de Usabilidade"',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 8), // 8 hours ago
    },
    {
      id: '6',
      type: 'comment_added',
      userId: '3',
      targetId: '2',
      targetType: 'task',
      title: 'Comentário adicionado',
      description: 'Collaborator User comentou na tarefa "Criação de Wireframes"',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 12), // 12 hours ago
    },
    {
      id: '7',
      type: 'task_updated',
      userId: '2',
      targetId: '5',
      targetType: 'task',
      title: 'Tarefa atualizada',
      description: 'Manager User atualizou o progresso da tarefa "Definição de Arquitetura"',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24), // 1 day ago
    },
    {
      id: '8',
      type: 'project_updated',
      userId: '1',
      targetId: '1',
      targetType: 'project',
      title: 'Projeto atualizado',
      description: 'Admin User atualizou o status do projeto "Website Redesign"',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 48), // 2 days ago
    }
  ]);

  // Mock comments
  const [comments] = useState<Comment[]>([
    {
      id: '1',
      taskId: '2',
      userId: '1',
      content: 'Ótimo trabalho nos wireframes! Só alguns ajustes menores na navegação.',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
    },
    {
      id: '2',
      taskId: '2',
      userId: '2',
      content: 'Obrigado pelo feedback! Vou fazer os ajustes hoje.',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 1),
    },
    {
      id: '3',
      taskId: '3',
      userId: '3',
      content: 'Preciso de mais detalhes sobre os componentes a serem implementados.',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 6),
    },
    {
      id: '4',
      taskId: '1',
      userId: '2',
      content: 'Análise concluída. Documento enviado para revisão.',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 12),
    }
  ]);

  const filteredActivities = useMemo(() => {
    if (filter === 'all') return activities;
    if (filter === 'tasks') return activities.filter(a => a.targetType === 'task');
    if (filter === 'projects') return activities.filter(a => a.targetType === 'project');
    if (filter === 'comments') return activities.filter(a => a.type === 'comment_added');
    return activities;
  }, [activities, filter]);

  const getActivityIcon = (type: ActivityItem['type']) => {
    switch (type) {
      case 'task_created': return <Plus className="w-4 h-4 text-blue-500" />;
      case 'task_updated': return <Edit className="w-4 h-4 text-yellow-500" />;
      case 'task_completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'project_created': return <FileText className="w-4 h-4 text-purple-500" />;
      case 'project_updated': return <GitCommit className="w-4 h-4 text-orange-500" />;
      case 'comment_added': return <MessageCircle className="w-4 h-4 text-indigo-500" />;
      case 'user_assigned': return <User className="w-4 h-4 text-teal-500" />;
      case 'deadline_changed': return <Calendar className="w-4 h-4 text-red-500" />;
      default: return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActivityColor = (type: ActivityItem['type']) => {
    switch (type) {
      case 'task_created': return 'bg-blue-50 border-blue-200';
      case 'task_updated': return 'bg-yellow-50 border-yellow-200';
      case 'task_completed': return 'bg-green-50 border-green-200';
      case 'project_created': return 'bg-purple-50 border-purple-200';
      case 'project_updated': return 'bg-orange-50 border-orange-200';
      case 'comment_added': return 'bg-indigo-50 border-indigo-200';
      case 'user_assigned': return 'bg-teal-50 border-teal-200';
      case 'deadline_changed': return 'bg-red-50 border-red-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const addComment = () => {
    if (!newComment.trim() || !selectedTask) return;
    
    console.log('Adding comment:', {
      taskId: selectedTask,
      userId: user?.id,
      content: newComment,
      timestamp: new Date()
    });
    
    setNewComment('');
    setSelectedTask('');
  };

  const getTargetName = (targetId: string, targetType: 'task' | 'project') => {
    if (targetType === 'task') {
      const task = tasks.find(t => t.id === targetId);
      return task?.title || 'Tarefa não encontrada';
    } else {
      const project = projects.find(p => p.id === targetId);
      return project?.name || 'Projeto não encontrado';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Atividades</h1>
          <p className="text-gray-600 mt-1">Acompanhe todas as atividades do projeto</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={() => setFilter('all')}>
            <Activity className="w-4 h-4 mr-2" />
            Todas
          </Button>
          <Button variant="outline" onClick={() => setFilter('tasks')}>
            <CheckCircle className="w-4 h-4 mr-2" />
            Tarefas
          </Button>
          <Button variant="outline" onClick={() => setFilter('projects')}>
            <FileText className="w-4 h-4 mr-2" />
            Projetos
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activity Feed */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  Feed de Atividades
                </CardTitle>
                <Badge variant="secondary">
                  {filteredActivities.length} atividades
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-96">
                <div className="space-y-4">
                  {filteredActivities.map((activity) => {
                    const activityUser = getUserById(activity.userId);
                    
                    return (
                      <div
                        key={activity.id}
                        className={`p-4 rounded-lg border-l-4 ${getActivityColor(activity.type)}`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-1">
                            {getActivityIcon(activity.type)}
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <Avatar className="w-6 h-6">
                                  <AvatarImage src={activityUser?.avatar} alt={activityUser?.name} />
                                  <AvatarFallback className="text-xs">
                                    {activityUser?.name.split(' ').map(n => n[0]).join('')}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-sm font-medium text-gray-900">
                                  {activityUser?.name}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">
                                {formatDistanceToNow(activity.timestamp, { 
                                  addSuffix: true, 
                                  locale: ptBR 
                                })}
                              </span>
                            </div>
                            
                            <div className="mt-2">
                              <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                              <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                              
                              <div className="mt-2 flex items-center space-x-2">
                                <Badge variant="outline" className="text-xs">
                                  {getTargetName(activity.targetId, activity.targetType)}
                                </Badge>
                                <Badge variant="secondary" className="text-xs">
                                  {activity.targetType === 'task' ? 'Tarefa' : 'Projeto'}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {filteredActivities.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Nenhuma atividade encontrada</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Comments Section */}
        <div className="space-y-6">
          {/* Add Comment */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Adicionar Comentário
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Selecionar Tarefa
                </label>
                <select 
                  value={selectedTask}
                  onChange={(e) => setSelectedTask(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Selecione uma tarefa</option>
                  {tasks.map(task => (
                    <option key={task.id} value={task.id}>
                      {task.title}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Escreva seu comentário..."
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-md text-sm resize-none"
                />
              </div>
              
              <Button 
                onClick={addComment}
                disabled={!newComment.trim() || !selectedTask}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600"
              >
                <Send className="w-4 h-4 mr-2" />
                Adicionar Comentário
              </Button>
            </CardContent>
          </Card>

          {/* Recent Comments */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Comentários Recentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-80">
                <div className="space-y-4">
                  {comments.map((comment) => {
                    const commentUser = getUserById(comment.userId);
                    const task = tasks.find(t => t.id === comment.taskId);
                    
                    return (
                      <div key={comment.id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-start space-x-3">
                          <Avatar className="w-6 h-6">
                            <AvatarImage src={commentUser?.avatar} alt={commentUser?.name} />
                            <AvatarFallback className="text-xs">
                              {commentUser?.name.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-900">
                                {commentUser?.name}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatDistanceToNow(comment.timestamp, { 
                                  addSuffix: true, 
                                  locale: ptBR 
                                })}
                              </span>
                            </div>
                            
                            <p className="text-sm text-gray-600 mt-1">{comment.content}</p>
                            
                            <div className="mt-2">
                              <Badge variant="outline" className="text-xs">
                                {task?.title}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Activity Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Resumo de Hoje
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Tarefas criadas</span>
                  <Badge variant="secondary">
                    {activities.filter(a => a.type === 'task_created' && 
                      a.timestamp.toDateString() === new Date().toDateString()).length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Tarefas concluídas</span>
                  <Badge variant="secondary">
                    {activities.filter(a => a.type === 'task_completed' && 
                      a.timestamp.toDateString() === new Date().toDateString()).length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Comentários</span>
                  <Badge variant="secondary">
                    {comments.filter(c => c.timestamp.toDateString() === new Date().toDateString()).length}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Atividades totais</span>
                  <Badge variant="secondary">
                    {activities.filter(a => a.timestamp.toDateString() === new Date().toDateString()).length}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};